<template>
  <div class="map-card">
    <!-- Map Content -->
    <div class="map-content">
      <div class="card-header">
        <h2>Interactive 2D Map</h2>
        <v-switch
          v-model="showPolygons"
          inset
          color="primary"
          hide-details
          density="compact"
          class="hotspot-switch"
          label="Hotspots"
        />
      </div>

      <div class="map-container">
        <l-map
          ref="map"
          @ready="onMapReady"
          :style="{ zIndex: 0, width: '100%', height: '100%' }"
          :zoom="currentZoom"
          :center="floorCenter"
          :bounds="floorBounds"
          :maxZoom="25"
          :minZoom="minZoom"
        >
          <l-image-overlay :url="floorPlanUrl" :bounds="floorBounds" />

          <!-- Markers for each POI -->
          <l-marker
            v-for="poi in POIS"
            :key="poi.id"
            :icon="getMarkerIcon(poi)"
            :lat-lng="[poi.locations[0].center.lat, poi.locations[0].center.long]"
          >
            <l-popup>
              <div class="popup-content">
                <h3>{{ poi.name }}</h3>
                <div class="popup-info">
                  <span>{{ poi.category?.name }}</span>
                  <span>{{ poi.time }}</span>
                  <span>{{ poi.capacity }}</span>
                  <span
                    class="status-badge"
                    :class="poi.status === 'booked' ? 'booked' : 'free'"
                  >
                    {{ poi.status === 'booked' ? 'Booked' : 'Free' }}
                  </span>
                </div>
              </div>
            </l-popup>
            <l-tooltip>
              {{ poi.name }}
            </l-tooltip>
          </l-marker>

          <!-- Polygons for each POI -->
          <template v-if="showPolygons">
            <l-polygon
              v-for="poi in POIS"
              :key="'polygon-' + poi.id"
              :lat-lngs="poi.locations[0].polygon.map(p => [p.lat, p.long])"
              :color="poi.status === 'booked' ? '#FF9999' : '#99FF99'"
              :fill-color="poi.status === 'booked' ? '#FFCCCC' : '#CCFFCC'"
              :fill-opacity="0.5"
              :weight="2"
            />
          </template>
        </l-map>
      </div>

      <div class="zoom-controls">
        <button @click="zoomIn" :class="{ selected: isZoomInActive }">＋</button>
        <button @click="zoomOut" :class="{ selected: isZoomOutActive }">－</button>
        <button @click="resetMap" :class="{ selected: isResetActive }">Reset</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import 'leaflet/dist/leaflet.css';
import { LMap, LTooltip, LImageOverlay, LMarker, LPopup, LPolygon } from '@vue-leaflet/vue-leaflet';
import { ref, onMounted, onBeforeUnmount, nextTick, computed } from 'vue';
import { latLngBounds, latLng } from 'leaflet';
import { getMarkerIcon } from '@/services/getMarkerIcon';

// Refs
const floorPlanUrl = ref('/assets/floorPlanBasic2D.png');
const showPolygons = ref(true);
const isZoomInActive = ref(false);
const isZoomOutActive = ref(false);
const isResetActive = ref(false);
const map = ref(null);
const POIS = ref([]);
const screenWidth = ref(window.innerWidth);

// Real geo-bounds
const floorBounds = latLngBounds(
  [53.54487831380667, 10.006931814357717], // SW
  [53.5459084479133, 10.00897590946902]    // NE
);

const floorCenter = latLng(
  (53.54487831380667 + 53.5459084479133) / 2,
  (10.006931814357717 + 10.00897590946902) / 2
);

// Responsive zoom levels based on screen size
const currentZoom = computed(() => {
  if (screenWidth.value <= 480) {
    return 18; // Most zoomed out for small mobile
  } else if (screenWidth.value <= 768) {
    return 19; // Medium zoom for tablets
  } else if (screenWidth.value <= 1024) {
    return 19.5; // Slightly zoomed out for small laptops
  } else {
    return 20; // Full zoom for desktop
  }
});

// Minimum zoom level
const minZoom = computed(() => {
  if (screenWidth.value <= 480) {
    return 17; // Allow more zoom out on mobile
  } else {
    return 18;
  }
});

let timeoutId = null;

// Load POIs immediately
const loadPOIS = async () => {
  try {
    const { pois } = await import('@/dummyData/pois');
    const { categories } = await import('@/dummyData/categories');
    const selectedFloorId = '365a50d0-a10a-4586-b79d-3a22577f85ca';

    const filteredPois = pois.filter(
      poi => poi.floors.ids[0] === selectedFloorId && poi?.name !== '2.32 PuMi'
    );

    filteredPois.forEach(poi => {
      poi.time = '06:00 - 22:00';
      poi.status = poi.name.includes('Toilet')
        ? 'free'
        : Math.random() > 0.5
          ? 'booked'
          : 'free';

      poi.category = poi.poiCategories.ids?.length > 0
        ? categories.find(cat => cat.id === poi.poiCategories.ids[0])
        : categories.find(cat => cat.name === 'Meeting room');

      POIS.value.push(poi);
    });
  } catch (error) {
    console.error('Error loading POIs:', error);
  }
};

// Zoom Controls
const zoomIn = () => {
  if (map.value?.mapObject) {
    map.value.mapObject.zoomIn();
    isZoomInActive.value = true;
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => (isZoomInActive.value = false), 300);
  }
};

const zoomOut = () => {
  if (map.value?.mapObject) {
    map.value.mapObject.zoomOut();
    isZoomOutActive.value = true;
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => (isZoomOutActive.value = false), 300);
  }
};

const resetMap = () => {
  if (map.value?.mapObject) {
    map.value.mapObject.setView(floorCenter, currentZoom.value);
    map.value.mapObject.fitBounds(floorBounds);
    isResetActive.value = true;
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => (isResetActive.value = false), 300);
  }
};

// Adjust map size and fit bounds with responsive zoom
const adjustMapSize = () => {
  nextTick(() => {
    setTimeout(() => {
      if (map.value?.mapObject) {
        map.value.mapObject.invalidateSize();
        map.value.mapObject.setView(floorCenter, currentZoom.value);
        map.value.mapObject.fitBounds(floorBounds);
      }
    }, 100);
  });
};

// Map ready callback
const onMapReady = () => {
  adjustMapSize();
};

// Handle window resize
const onResize = () => {
  screenWidth.value = window.innerWidth;
  adjustMapSize();
};

// Lifecycle hooks
onMounted(async () => {
  // Load POIs immediately on mount
  await loadPOIS();
  
  // Give time for map to render
  await nextTick();
  
  setTimeout(() => {
    adjustMapSize();
  }, 300);

  // Add event listeners
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  window.removeEventListener('orientationchange', onResize);
  if (timeoutId) clearTimeout(timeoutId);
});
</script>

<style scoped>
.map-card {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: transparent;
  box-sizing: border-box;
}

.map-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  box-sizing: border-box;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
}

.card-header h2 {
  color: #ffffff;
  margin: 0;
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 600;
}

.hotspot-switch {
  margin: 0;
  padding: 0;
  transform: scale(0.9);
}

.map-container {
  flex: 1;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 0; /* Important for flex child */
}

/* Ensure Leaflet container fills parent */
.map-container :deep(.leaflet-container) {
  width: 100% !important;
  height: 100% !important;
  background: transparent;
}

/* Make map controls more touch-friendly on mobile */
.map-container :deep(.leaflet-control-zoom) {
  margin: 10px;
}

.map-container :deep(.leaflet-control-zoom a) {
  width: 36px;
  height: 36px;
  line-height: 36px;
  font-size: 20px;
}

/* Adjust marker size for mobile */
@media (max-width: 768px) {
  .map-container :deep(.leaflet-marker-icon) {
    transform: scale(0.85) !important;
  }
}

@media (max-width: 480px) {
  .map-container :deep(.leaflet-marker-icon) {
    transform: scale(0.75) !important;
  }
}

.zoom-controls {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding-top: 1.5rem;
  flex-shrink: 0;
}

.zoom-controls button {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 80px;
  backdrop-filter: blur(10px);
  touch-action: manipulation; /* Better touch response */
}

.zoom-controls button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.zoom-controls button:active {
  transform: translateY(0);
}

.zoom-controls button.selected {
  background: rgba(102, 126, 234, 0.3);
  border-color: #667eea;
  box-shadow: 0 0 10px rgba(102, 126, 234, 0.5);
}

/* Popup */
.popup-content {
  padding: 0.5rem;
  min-width: 180px;
}

.popup-content h3 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  color: #2c3e50;
}

.popup-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #5a6c7d;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  margin-top: 0.25rem;
  width: fit-content;
}

.status-badge.booked {
  background: #ffebee;
  color: #c62828;
}

.status-badge.free {
  background: #e8f5e9;
  color: #2e7d32;
}

/* Tablet Responsive */
@media (max-width: 1024px) {
  .map-content {
    padding: 1.25rem;
  }

  .card-header {
    margin-bottom: 1.25rem;
  }

  .zoom-controls {
    gap: 0.875rem;
    padding-top: 1.25rem;
  }

  .zoom-controls button {
    padding: 0.65rem 1.25rem;
    font-size: 0.95rem;
    min-width: 70px;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .map-content {
    padding: 1rem;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .card-header h2 {
    font-size: 1.25rem;
  }

  .hotspot-switch {
    transform: scale(0.85);
  }

  .map-container {
    border-radius: 10px;
  }

  .zoom-controls {
    gap: 0.75rem;
    padding-top: 1rem;
    flex-wrap: wrap;
  }

  .zoom-controls button {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
    min-width: 60px;
  }

  .popup-content {
    min-width: 150px;
  }

  .popup-content h3 {
    font-size: 0.9rem;
  }

  .popup-info {
    font-size: 0.8rem;
  }
}

/* Small Mobile Responsive */
@media (max-width: 480px) {
  .map-content {
    padding: 0.75rem;
  }

  .card-header {
    gap: 0.5rem;
    margin-bottom: 0.875rem;
  }

  .card-header h2 {
    font-size: 1.125rem;
  }

  .hotspot-switch {
    transform: scale(0.8);
  }

  .map-container {
    border-radius: 8px;
  }

  .zoom-controls {
    gap: 0.5rem;
    padding-top: 0.875rem;
  }

  .zoom-controls button {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    min-width: 50px;
  }

  .popup-content {
    min-width: 130px;
    padding: 0.375rem;
  }

  .popup-content h3 {
    font-size: 0.85rem;
    margin-bottom: 0.375rem;
  }

  .popup-info {
    font-size: 0.75rem;
    gap: 0.2rem;
  }

  .status-badge {
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
  }
}

/* Landscape Mobile */
@media (max-width: 768px) and (orientation: landscape) {
  .map-content {
    padding: 0.75rem;
  }

  .card-header {
    flex-direction: row;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .zoom-controls {
    padding-top: 0.75rem;
  }

  .zoom-controls button {
    padding: 0.5rem 1rem;
    min-width: 55px;
  }
}

/* Touch devices - increase touch targets */
@media (hover: none) and (pointer: coarse) {
  .zoom-controls button {
    min-height: 44px; /* iOS minimum touch target */
    min-width: 44px;
  }

  .map-container :deep(.leaflet-control-zoom a) {
    width: 40px;
    height: 40px;
    line-height: 40px;
  }
}
</style>